import { motion } from "framer-motion";
import { TrendingUp, ShieldCheck, Users } from "lucide-react";
import { useLanguage } from "@/hooks/useLanguage";

export const ImpactStats = () => {
  const { language } = useLanguage();

  const content = {
    fr: {
      title: "L'impact OuraTrust",
      stats: [
        {
          value: "+25%",
          label: "d'avis positifs en moyenne",
          sublabel: "dès le premier mois",
          icon: TrendingUp,
        },
        {
          value: "98%",
          label: "des retours clients traités",
          sublabel: "via notre hub centralisé",
          icon: ShieldCheck,
        },
        {
          value: "500+",
          label: "professionnels nous font confiance",
          sublabel: "commerces, santé, services",
          icon: Users,
        },
      ],
    },
    en: {
      title: "The OuraTrust Impact",
      stats: [
        {
          value: "+25%",
          label: "more positive reviews",
          sublabel: "in the first month",
          icon: TrendingUp,
        },
        {
          value: "98%",
          label: "of customer feedback handled",
          sublabel: "through our centralized hub",
          icon: ShieldCheck,
        },
        {
          value: "500+",
          label: "local businesses & professionals",
          sublabel: "healthcare, retail, services",
          icon: Users,
        },
      ],
    },
  };

  const t = content[language];

  return (
    <section className="py-20 relative overflow-hidden">
      {/* Gradient Background */}
      <div className="absolute inset-0 bg-gradient-to-r from-primary via-primary/90 to-primary" />
      
      {/* Decorative Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Title */}
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-3xl md:text-4xl font-bold text-white text-center mb-16"
        >
          {t.title}
        </motion.h2>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {t.stats.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.15 }}
              className="text-center"
            >
              {/* Icon */}
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm mb-4">
                <stat.icon className="text-white" size={28} />
              </div>

              {/* Value */}
              <motion.div
                initial={{ scale: 0.5 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.15 + 0.2, type: "spring" }}
                className="text-5xl md:text-6xl font-bold text-white mb-2"
              >
                {stat.value}
              </motion.div>

              {/* Label */}
              <p className="text-lg text-white/90 font-medium mb-1">
                {stat.label}
              </p>
              <p className="text-sm text-white/70">
                {stat.sublabel}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
